import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Icon } from "../common/Icon";

export interface LadderConfigValues {
  nameCs: string;
  nameEn: string;
  calculateElo: boolean;
  discountCloseLoss: boolean;
  calculateRating: boolean;
  awardAchievements: boolean;
}

export const DEFAULT_LADDER_CONFIG: LadderConfigValues = {
  nameCs: "",
  nameEn: "",
  calculateElo: true,
  discountCloseLoss: true,
  calculateRating: true,
  awardAchievements: true,
};

interface ToggleRowProps {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

function ToggleRow({ id, label, hint, checked, onChange, disabled }: ToggleRowProps) {
  return (
    <div className={`ladder-toggle-row${disabled ? " ladder-toggle-row-disabled" : ""}`}>
      <div className="ladder-toggle-copy">
        <label htmlFor={id} className="ladder-toggle-label">
          {label}
        </label>
        <div className="ladder-toggle-hint">{hint}</div>
      </div>
      <Form.Check
        type="switch"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="ladder-toggle-switch"
      />
    </div>
  );
}

interface LadderConfigFieldsProps {
  value: LadderConfigValues;
  onChange: (patch: Partial<LadderConfigValues>) => void;
  /** Keeps element ids unique should both forms ever mount at once. */
  idPrefix?: string;
  autoFocusName?: boolean;
}

/**
 * The shared ladder configuration form body: name fields plus the collapsible advanced
 * toggles. Used by both the create page (NewLadder) and the settings page (LadderSettings)
 * so the two can't drift apart.
 */
export function LadderConfigFields({
  value,
  onChange,
  idPrefix = "ladder",
  autoFocusName = false,
}: LadderConfigFieldsProps) {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <>
      <h6 className="new-ladder-section-title">{t("basicInformation")}</h6>
      <div className="new-ladder-name-grid">
        <Form.Group controlId={`${idPrefix}NameCs`}>
          <Form.Label>{t("ladderNameCs")}</Form.Label>
          <Form.Control
            value={value.nameCs}
            onChange={(e) => onChange({ nameCs: e.target.value })}
            required
            autoFocus={autoFocusName}
          />
        </Form.Group>
        <Form.Group controlId={`${idPrefix}NameEn`}>
          <Form.Label>{t("ladderNameEn")}</Form.Label>
          <Form.Control
            value={value.nameEn}
            onChange={(e) => onChange({ nameEn: e.target.value })}
            required
          />
        </Form.Group>
      </div>

      <Button
        variant="link"
        className="new-ladder-advanced-toggle"
        onClick={() => setShowAdvanced((v) => !v)}
        type="button"
      >
        <Icon
          name={showAdvanced ? "ChevronDown" : "ChevronRight"}
          className="new-ladder-advanced-caret"
        />
        {showAdvanced ? t("hideAdvancedSettings") : t("showAdvancedSettings")}
      </Button>

      {showAdvanced ? (
        <div className="new-ladder-advanced">
          <ToggleRow
            id={`${idPrefix}-toggle-elo`}
            label={t("calculateElo")}
            hint={t("calculateEloHint")}
            checked={value.calculateElo}
            onChange={(v) => onChange({ calculateElo: v })}
          />
          <ToggleRow
            id={`${idPrefix}-toggle-close-loss`}
            label={t("discountCloseLoss")}
            hint={t("discountCloseLossHint")}
            checked={value.discountCloseLoss}
            onChange={(v) => onChange({ discountCloseLoss: v })}
            disabled={!value.calculateElo}
          />
          <ToggleRow
            id={`${idPrefix}-toggle-rating`}
            label={t("calculateRating")}
            hint={t("calculateRatingHint")}
            checked={value.calculateRating}
            onChange={(v) => onChange({ calculateRating: v })}
          />
          <ToggleRow
            id={`${idPrefix}-toggle-achievements`}
            label={t("awardAchievements")}
            hint={t("awardAchievementsHint")}
            checked={value.awardAchievements}
            onChange={(v) => onChange({ awardAchievements: v })}
          />
        </div>
      ) : null}
    </>
  );
}
